import { css, styled } from '@zeiss/beyond-online-react';
import { useEffect, useRef, useState } from 'react';

interface EntryContainerProps {
  $hasCtaBelow?: boolean;
  $isError?: boolean;
}

interface InputProps {
  $isError?: boolean;
  $isFocused?: boolean;
}

const CtaContainer = styled.div`
  /* To left-align with inputs */
  padding: 0 var(--spacing-xs);

  @media (width >= 768px) {
    display: flex;
    flex-wrap: wrap;
    column-gap: var(--spacing-s);
    justify-content: space-between;
  }
`;

const EntryContainer = styled.div<EntryContainerProps>`
  display: inline-block;
  width: fit-content;

  /* Deviates from Figma. Looks better. */
  padding: var(--spacing-xs);

  ${({ $isError }) =>
    $isError &&
    css`
      /* stylelint-disable-next-line custom-property-pattern */
      background-color: var(--color-global-background-error_subtle);
    `}

  ${({ $hasCtaBelow, $isError }) =>
    $hasCtaBelow &&
    !$isError &&
    css`
      /* Consistent spacing to the cta, whether isError or not */
      margin-bottom: 1.375rem;
    `}
`;

const ErrorMessage = styled.p`
  padding-top: var(--spacing-2xs);
  margin: var(--spacing-0);
  font: var(--text-body-xs);
  color: var(--color-global-text-error);
`;

const InputContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: var(--spacing-xs);
  align-items: stretch;
  height: 3.25rem;

  @media (width >= 768px) {
    gap: var(--spacing-s);
    height: 5rem;
  }

  @media (width >= 1440px) {
    height: 6rem;
  }
`;

const OtpContainer = styled.div`
  /* To align with Beyond inputs */
  margin-inline-start: calc(var(--spacing-xs) * -1);
`;

const StyledInput = styled.input<InputProps>`
  width: 100%;
  font: var(--text-hl-xs);
  text-align: center;
  outline: 1px solid transparent;
  background-color: transparent;
  border-radius: var(--border-radius-m);

  ${({ $isError, $isFocused }) => {
    if ($isError) {
      if ($isFocused) {
        return css`
          color: var(--color-global-text-error);
          border: 2px solid var(--color-global-border-error);
        `;
      }
      return css`
        color: var(--color-global-text-error);
        border: 1px solid var(--color-global-border-error);
      `;
    }

    if ($isFocused) {
      return css`
        color: var(--color-global-text-subtle);
        border: 1px solid var(--color-global-border-focus);
      `;
    }

    return css`
      color: var(--color-global-text-subtle);
      border: 1px solid var(--color-global-border-subtle);
    `;
  }}
`;

export interface OtpInputProps {
  ctaContent?: React.ReactNode;
  disabled?: boolean;
  error?: boolean | string;
  length?: number;
  loading?: boolean;
  onComplete: (otp: string) => void;
}

type DocumentDirection = 'ltr' | 'rtl';

/**
 * Find the next closest direction-attribute set within the parent hierarchy.
 *
 * Detects both,
 * - dir-attributes on any DOM node in the parent-hierarchy
 * - document-level direction
 *
 * Based on {@link https://dev.azure.com/ZEISSgroup/ZDP_ODS/_git/ods.ui?path=/packages/beyond-online-react/src/hooks/useRtl.ts&version=GT4.32.0-beyond-online-react&line=17&lineEnd=23&lineStartColumn=1&lineEndColumn=2&lineStyle=plain&_a=contents | Beyond Online React - useRtl}
 */
function getElementDirection(element: HTMLElement | null): DocumentDirection {
  if (!element) return 'ltr';

  // "[dir]:not([dir=""])" => ignores empty dir attributes
  // Empty dir attributes fall back to document / browser default, which is unknown
  const directionElement = element.closest('[dir]:not([dir=""])');

  return directionElement?.getAttribute('dir') as DocumentDirection;
}

/**
 * OTP (One-Time Password) component for verification code input.
 *
 * Error messages are displayed below the input fields, just like in Beyond
 * Form components.
 *
 * Figma design is not responsive and assumes a fixed width, which is not how it
 * is used in the content container in our apps. Therefore there are additional
 * rules for the 1440px breakpoint to make it look decent.
 */
export const Otp = ({
  ctaContent,
  disabled,
  error,
  length = 6,
  loading,
  onComplete,
}: OtpInputProps) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement[]>(Array(length).fill(null));
  const containerRef = useRef<HTMLDivElement>(null);

  const [hasError, setHasError] = useState(!!error);

  const errorMessage = typeof error === 'string' ? error : '';

  // Sync internal error state with error prop
  useEffect(() => {
    setHasError(!!error);
  }, [error]);

  useEffect(() => {
    inputRef.current[0].focus();
  }, []);

  // Clear OTP inputs when error is displayed
  useEffect(() => {
    if (hasError) {
      setOtp(Array(length).fill(''));
      inputRef.current[0].focus();
    }
  }, [hasError, length]);

  const handleChange = (value: string, index: number) => {
    const newOtp = [...otp];
    const trimmedValue = value.replace(/\s+/g, '');

    // Reset error
    if (newOtp.length > 0 && hasError) {
      setHasError(false);
    }

    // allow user to replace value
    if (trimmedValue.length === 2) {
      newOtp[index] = trimmedValue.substring(1, 2);
    } else if (trimmedValue.length >= length) {
      const reducedValue = trimmedValue.substring(0, length);
      setOtp(reducedValue.split(''));
      onComplete(reducedValue);
      inputRef.current[length - 1].focus();
      return;
    } else {
      newOtp[index] = trimmedValue.substring(0, 1);
    }

    if (trimmedValue.length > 0 && index < length - 1) {
      inputRef.current[index + 1].focus();
    }

    if (newOtp.every((value) => value !== '')) {
      onComplete(newOtp.join(''));
    }

    setOtp(newOtp);
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const newOtp = [...otp];
    const clipboardValue = event.clipboardData.getData('text/plain');
    const trimmedValue = clipboardValue.replace(/\s+/g, '');

    const splittedValues = trimmedValue.split('');

    // full or more value is pasted
    if (trimmedValue.length >= length) {
      const reducedValue = trimmedValue.substring(0, length);
      const splittedValues = reducedValue.split('');
      setOtp(splittedValues);
      onComplete(reducedValue);
      inputRef.current[length - 1].focus();
    }
    // partial value is pasted
    if (trimmedValue.length < length) {
      splittedValues.forEach((val, i) => {
        newOtp[i] = val;
        inputRef.current[i + 1].focus();
      });
      setOtp(newOtp);
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    currentIndex: number,
  ) => {
    const isRTL = getElementDirection(containerRef.current) === 'rtl';

    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case ' ':
        event.preventDefault();
        break;
      case 'ArrowLeft': {
        event.preventDefault();
        // In RTL: left arrow moves to next field, in LTR: moves to previous
        const targetIndex = isRTL ? currentIndex + 1 : currentIndex - 1;

        // Make sure target index is within bounds
        if (targetIndex >= 0 && targetIndex < length) {
          inputRef.current[targetIndex]?.focus();
          inputRef.current[targetIndex]?.select();
        }
        break;
      }
      case 'ArrowRight': {
        event.preventDefault();
        // In RTL: right arrow moves to previous field, in LTR: moves to next
        const targetIndex = isRTL ? currentIndex - 1 : currentIndex + 1;

        // Make sure target index is within bounds
        if (targetIndex >= 0 && targetIndex < length) {
          inputRef.current[targetIndex]?.focus();
          inputRef.current[targetIndex]?.select();
        }
        break;
      }
      case 'Delete':
      case 'Backspace':
        event.preventDefault();
        handleChange('', currentIndex);
        if (currentIndex > 0) {
          inputRef.current[currentIndex - 1].focus();
          inputRef.current[currentIndex - 1].select();
        }
        break;

      default:
        break;
    }
  };

  return (
    <OtpContainer ref={containerRef}>
      <EntryContainer $hasCtaBelow={!!ctaContent} $isError={hasError}>
        <InputContainer>
          {Array.from({ length }, (_, index) => (
            <StyledInput
              $isError={hasError}
              $isFocused={focusedIndex === index}
              autoComplete={index === 0 ? 'one-time-code' : undefined}
              disabled={disabled || loading}
              inputMode="numeric"
              key={index}
              onBlur={() => setFocusedIndex(null)}
              onChange={(e) => handleChange(e.target.value, index)}
              onFocus={() => setFocusedIndex(index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={(e) => handlePaste(e)}
              pattern="[0-9]*"
              ref={(ref) => (ref ? (inputRef.current[index] = ref) : null)}
              value={otp[index]}
            />
          ))}
        </InputContainer>
        {hasError && errorMessage && (
          <ErrorMessage>{errorMessage}</ErrorMessage>
        )}
      </EntryContainer>
      {ctaContent ? <CtaContainer>{ctaContent}</CtaContainer> : null}
    </OtpContainer>
  );
};
